import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, vShow as _vShow, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "bg-white rounded-lg shadow-sm border border-gray-200 py-4 px-4 mb-4" }
const _hoisted_2 = { class: "mb-2 flex items-center justify-between gap-2" }
const _hoisted_3 = { class: "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" }
const _hoisted_4 = ["max"]
const _hoisted_5 = ["disabled"]
const _hoisted_6 = ["value"]
const _hoisted_7 = { class: "flex justify-end pt-1 pb-2" }

import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import type { OrderFilters } from "@/types/order";
import { getCountriesFromOrders } from "@/api/orders";

interface Props {
  initialFilters?: OrderFilters;
}

interface Emits {
  (e: "filters", filters: OrderFilters): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OrderFilters',
  props: {
    initialFilters: { default: () => ({
    status: "",
    orderId: "",
    carrier: "",
    orderDate: "",
    shippingDate: "",
    buyer: "",
    origin: "",
  }) }
  },
  emits: ["filters"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Default empty filters for reset
const defaultFilters: OrderFilters = {
  status: "",
  orderId: "",
  carrier: "",
  orderDate: "",
  shippingDate: "",
  buyer: "",
  origin: "",
};

const filters = ref<OrderFilters>({ ...props.initialFilters });
const isCollapsed = ref(true);

// Appliquer les filtres initiaux quand ils changent (sans émettre immédiatement)
watch(
  () => props.initialFilters,
  (newFilters) => {
    if (newFilters) {
      filters.value = { ...newFilters };
    }
  },
  { immediate: true },
);

// Watch sur les filtres pour émettre automatiquement avec debounce
let isInitialized = false;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastEmittedFilters: OrderFilters | null = null;

watch(
  () => filters.value,
  (newFilters, oldFilters) => {
    // Ne pas émettre si le composant n'est pas encore initialisé
    if (!isInitialized) return;

    // Vérifier si les filtres ont vraiment changé
    const filtersChanged =
      !lastEmittedFilters ||
      JSON.stringify(newFilters) !== JSON.stringify(lastEmittedFilters);

    if (!filtersChanged) return;

    // Annuler le timer précédent s'il existe
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Attendre 100 avant d'émettre les filtres
    debounceTimer = setTimeout(() => {
      if (newFilters && typeof newFilters === "object") {
        lastEmittedFilters = { ...newFilters };
        emit("filters", { ...newFilters });
      }
    }, 100);
  },
  { deep: true },
);

const maxDate = computed(() => {
  const today = new Date();
  return today.toISOString().split("T")[0];
});

// Computed property to check if any filter is active
const hasActiveFilters = computed(() => {
  if (!filters.value || typeof filters.value !== "object") {
    return false;
  }
  return Object.values(filters.value).some(
    (value) => value !== null && value !== undefined && value !== "",
  );
});

const countries = ref<Array<{ id_pays: number; texte_fr: string }>>([]);
const isLoadingCountries = ref(false);

const handleSubmit = (event?: Event) => {
  // Empêcher le comportement par défaut du formulaire si l'événement est fourni
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // S'assurer que filters.value existe et est un objet
  if (!filters.value || typeof filters.value !== "object") {
    filters.value = { ...defaultFilters };
  }

  emit("filters", { ...filters.value });
};

const handleReset = () => {
  filters.value = { ...defaultFilters };
  emit("filters", { ...defaultFilters });
};

// Charger les pays depuis la base de données
const loadCountries = async () => {
  try {
    isLoadingCountries.value = true;
    const pays = await getCountriesFromOrders();
    countries.value = pays;
  } catch (error) {
    // En cas d'erreur, on garde un tableau vide
    countries.value = [];
  } finally {
    isLoadingCountries.value = false;
  }
};

onMounted(() => {
  // Charger les pays depuis la DB
  loadCountries();

  // Marquer comme initialisé après un court délai pour éviter l'émission initiale
  setTimeout(() => {
    isInitialized = true;
  }, 100);

  // Ouvrir par défaut sur desktop, fermer sur mobile
  const handleResize = () => {
    isCollapsed.value = window.innerWidth < 768;
  };
  handleResize();
  window.addEventListener("resize", handleResize);
  onUnmounted(() => {
    window.removeEventListener("resize", handleResize);
  });
});

onUnmounted(() => {
  // Cleanup debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[9] || (_cache[9] = _createElementVNode("h3", { class: "text-sm font-semibold text-black" }, "Filtrer la liste", -1)),
      _createElementVNode("button", {
        type: "button",
        class: "flex items-center gap-1 text-xs font-medium text-black px-2 py-1 border border-gray-300 rounded md:hidden",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (isCollapsed.value = !isCollapsed.value))
      }, [
        _createElementVNode("i", {
          class: _normalizeClass(isCollapsed.value ? 'fas fa-chevron-down' : 'fas fa-chevron-up')
        }, null, 2),
        _createTextVNode(" " + _toDisplayString(isCollapsed.value ? "Afficher" : "Masquer"), 1)
      ])
    ]),
    _withDirectives(_createElementVNode("div", null, [
      _createElementVNode("form", {
        onSubmit: [
          _withModifiers(handleSubmit, ["prevent"]),
          _cache[8] || (_cache[8] = _withModifiers(() => {}, ["stop"]))
        ],
        class: "space-y-2 pt-2",
        novalidate: "",
        autocomplete: "off"
      }, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", null, [
            _cache[10] || (_cache[10] = _createElementVNode("label", {
              for: "orderId",
              class: "block text-xs font-medium text-black mb-0.5"
            }, " N° commande ", -1)),
            _withDirectives(_createElementVNode("input", {
              id: "orderId",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.value.orderId) = $event)),
              type: "text",
              placeholder: "N° commande",
              autocomplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, null, 512), [
              [
                _vModelText,
                filters.value.orderId,
                void 0,
                { trim: true }
              ]
            ])
          ]),
          _createElementVNode("div", null, [
            _cache[12] || (_cache[12] = _createElementVNode("label", {
              for: "status",
              class: "block text-xs font-medium text-black mb-0.5"
            }, " Statut ", -1)),
            _withDirectives(_createElementVNode("select", {
              id: "status",
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.status) = $event)),
              autocomplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, _cache[11] || (_cache[11] = [
              _createStaticVNode("<option value=\"\">Tous</option><option value=\"1\">En attente</option><option value=\"2\">Expédiée</option><option value=\"3\">Annulée</option><option value=\"4\">Confirmée</option><option value=\"14\">Reçu au dépôt</option>", 6)
            ]), 512), [
              [_vModelSelect, filters.value.status]
            ])
          ]),
          _createElementVNode("div", null, [
            _cache[13] || (_cache[13] = _createElementVNode("label", {
              for: "buyer",
              class: "block text-xs font-medium text-black mb-0.5"
            }, " Client ", -1)),
            _withDirectives(_createElementVNode("input", {
              id: "buyer",
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((filters.value.buyer) = $event)),
              type: "text",
              placeholder: "Nom client",
              autocomplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, null, 512), [
              [
                _vModelText,
                filters.value.buyer,
                void 0,
                { trim: true }
              ]
            ])
          ]),
          _createElementVNode("div", null, [
            _cache[14] || (_cache[14] = _createElementVNode("label", { class: "block text-xs font-medium text-black mb-0.5" }, " Date cmd ", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filters.value.orderDate) = $event)),
              type: "date",
              max: maxDate.value,
              autocomplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, null, 8, _hoisted_4), [
              [_vModelText, filters.value.orderDate]
            ])
          ]),
          _createElementVNode("div", null, [
            _cache[15] || (_cache[15] = _createElementVNode("label", { class: "block text-xs font-medium text-black mb-0.5" }, " Date exp. ", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filters.value.shippingDate) = $event)),
              type: "date",
              autocomplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, null, 512), [
              [_vModelText, filters.value.shippingDate]
            ])
          ]),
          _createElementVNode("div", null, [
            _cache[17] || (_cache[17] = _createElementVNode("label", {
              for: "carrier",
              class: "block text-xs font-medium text-black mb-0.5"
            }, " Transporteur ", -1)),
            _withDirectives(_createElementVNode("select", {
              id: "carrier",
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((filters.value.carrier) = $event)),
              autocomplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, _cache[16] || (_cache[16] = [
              _createStaticVNode("<option value=\"\">-- Sélection transporteur --</option><optgroup label=\"Transporteur\"><option value=\"colissimo\">Colissimo</option><option value=\"dpd\">DPD</option><option value=\"ups\">UPS</option><option value=\"gls\">GLS</option><option value=\"chronopost\">Chronopost</option><option value=\"dhl\">DHL</option><option value=\"fedex\">Fedex</option><option value=\"bollore\">Bolloré</option><option value=\"transportAcheteur\">Transporteur Acheteur</option></optgroup><optgroup label=\"Personaliser\"><option value=\"UPSgroupage\">UPS ( Groupage )</option><option value=\"UPSstandardGroupage\"> UPS Standard ( Groupage ) </option><option value=\"FedexGroupage\">Fedex ( Groupage )</option><option value=\"ChronopostGroupage\"> Chrono Multi ( Groupage ) </option><option value=\"GLSmulti\">GLS ( Multi )</option><option value=\"gpx\">GPX ( Groupage )</option><option value=\"ocx\">OCX ( Groupage )</option></optgroup>", 3)
            ]), 512), [
              [_vModelSelect, filters.value.carrier]
            ])
          ]),
          _createElementVNode("div", null, [
            _cache[19] || (_cache[19] = _createElementVNode("label", {
              for: "origin",
              class: "block text-xs font-medium text-black mb-0.5"
            }, " Pays ", -1)),
            _withDirectives(_createElementVNode("select", {
              id: "origin",
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((filters.value.origin) = $event)),
              autocomplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500",
              disabled: isLoadingCountries.value
            }, [
              _cache[18] || (_cache[18] = _createElementVNode("option", { value: "" }, "Tous", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(countries.value, (country) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: country.id_pays,
                  value: country.id_pays.toString()
                }, _toDisplayString(country.texte_fr), 9, _hoisted_6))
              }), 128))
            ], 8, _hoisted_5), [
              [_vModelSelect, filters.value.origin]
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          (hasActiveFilters.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                onClick: handleReset,
                class: "inline-flex items-center px-3.5 py-1 text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-red-500"
              }, _cache[20] || (_cache[20] = [
                _createElementVNode("i", { class: "fas fa-undo mr-1 text-xs" }, null, -1),
                _createTextVNode(" Réinitialiser ")
              ])))
            : _createCommentVNode("", true)
        ])
      ], 32)
    ], 512), [
      [_vShow, !isCollapsed.value]
    ])
  ]))
}
}

})